/** /tenant_requests/ — requests from tenants (backend plan 2026-10-17-dashboard-self-signup-invite-links
 *  §10). `go_live` is asked by a self-signup client from its dashboard's Onboarding page; ops
 *  approve (the same go live as the easy-onboarding Go live, with the policy and the re-sync they
 *  choose) or reject it with a note the client sees. The tenant lifecycle kinds (`close`,
 *  `release`, `reopen`; tenant retirement) are listed here too but decided on the tenant's
 *  Lifecycle tab (`api/tenantLifecycle.ts`).
 *
 *  Every endpoint needs `console_top_manage` + `console_preflight_manage` (today's Go live
 *  permissions). The easy-onboarding state carries the tenant's request too
 *  (`ConsoleEasyOnboarding.go_live_request`), so the onboarding page needs no read of its own.
 *
 *  Shapes come from the generated spec types; never hand-mirror them. */
import { apiGet, apiPost, ConsoleApiError, extendedListConfig } from './client';
import type { components } from './types';
import type { PaginatedResponse } from './users';
import type { WireNode } from '@/components/QuerySearch';

type Schemas = components['schemas'];

export type TenantRequest = Schemas['ConsoleTenantRequest'];
export type TenantRequestStatus = Schemas['ConsoleTenantRequestStatus'];
export type TenantRequestKind = TenantRequest['kind'];
export type TenantRequestApprove = Schemas['ConsoleTenantRequestApprove'];
export type TenantRequestApproveResult = Schemas['ConsoleTenantRequestApproveResult'];
export type TenantRequestReject = Schemas['ConsoleTenantRequestReject'];
export type TenantRequestErrorBody = Schemas['ConsoleTenantRequestError'];
export type TenantRequestErrorCode = TenantRequestErrorBody['code'];
/** The signup invite a request's tenant was created from (null for a tenant ops created). */
export type TenantRequestInvite = NonNullable<TenantRequest['invite']>;

/** Every status in lifecycle order; `satisfies` keeps it exhaustive against the spec enum. */
export const TENANT_REQUEST_STATUS_LABELS = {
  pending: 'Pending',
  approved: 'Approved',
  applying: 'Applying',
  applied: 'Applied',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
  superseded: 'Superseded',
  failed: 'Failed',
} as const satisfies Record<TenantRequestStatus, string>;

export const TENANT_REQUEST_STATUSES = Object.keys(
  TENANT_REQUEST_STATUS_LABELS,
) as TenantRequestStatus[];

/** The `status` filter's extra value: pending, approved or applying. */
export const OPEN_STATUS = 'open';

export const TENANT_REQUEST_KIND_LABELS = {
  go_live: 'Go live',
  close: 'Close',
  release: 'Release code',
  reopen: 'Reopen',
} as const satisfies Record<TenantRequestKind, string>;

export const TENANT_REQUEST_KINDS = Object.keys(TENANT_REQUEST_KIND_LABELS) as TenantRequestKind[];

/** The tenant lifecycle kinds (tenant retirement): ops ask for them on the tenant's Lifecycle tab
 *  and decide them there — the go-live approve / reject endpoints answer 409 `wrong_kind`. */
export const LIFECYCLE_REQUEST_KINDS = ['close', 'release', 'reopen'] as const satisfies readonly TenantRequestKind[];
export type LifecycleRequestKind = (typeof LIFECYCLE_REQUEST_KINDS)[number];

export function isLifecycleKind(kind: TenantRequestKind): kind is LifecycleRequestKind {
  return (LIFECYCLE_REQUEST_KINDS as readonly string[]).includes(kind);
}

/** The decision note's limit (approve and reject). */
export const NOTE_MAX = 2000;

/** Under one root, so a `['tenantRequests']` invalidation reaches the grid. */
export const tenantRequestsKeys = {
  all: ['tenantRequests'] as const,
  list: () => ['tenantRequests', 'list'] as const,
};

export interface TenantRequestListParams {
  /** Extended statement-budget tier for this search (audited server-side). */
  timeout?: 'extended';
  page?: number;
  page_size?: number;
  /** id|requested_ts|decided_ts|status|kind ('-' desc). Default -requested_ts. */
  ordering?: string;
  filters?: WireNode[];
}

function toQuery(p: TenantRequestListParams): string {
  const usp = new URLSearchParams();
  if (p.page) usp.set('page', String(p.page));
  if (p.page_size) usp.set('page_size', String(p.page_size));
  if (p.ordering) usp.set('ordering', p.ordering);
  if (p.timeout) usp.set('timeout', p.timeout);
  // Omit `filters` entirely when empty (never send `filters=[]`).
  if (p.filters && p.filters.length > 0) usp.set('filters', JSON.stringify(p.filters));
  return usp.toString();
}

export async function list(
  p: TenantRequestListParams = {},
): Promise<PaginatedResponse<TenantRequest>> {
  const qs = toQuery(p);
  return apiGet(`/tenant_requests/${qs ? `?${qs}` : ''}`, extendedListConfig(p.timeout));
}

export async function retrieve(id: number): Promise<TenantRequest> {
  return apiGet(`/tenant_requests/${id}/`);
}

/** Approve a pending request: the go live runs with the body's policy and re-sync — now (200,
 *  the request `applied`) or after the `easy_go_live` re-sync task (202, `applying`). A Go live
 *  refusal (400 / 409) leaves the request pending; 409 `request_not_pending` means someone
 *  decided it (or the client withdrew it) meanwhile. */
export async function approve(
  id: number,
  body: TenantRequestApprove,
): Promise<TenantRequestApproveResult> {
  return apiPost(`/tenant_requests/${id}/approve/`, body);
}

/** Reject a pending request; the note is shown to the client, who may ask again. */
export async function reject(id: number, note: string): Promise<TenantRequest> {
  return apiPost(`/tenant_requests/${id}/reject/`, { note } satisfies TenantRequestReject);
}

/** The request-specific refusal (409): decided, withdrawn or replaced meanwhile. */
export const REQUEST_NOT_PENDING = 'request_not_pending' satisfies TenantRequestErrorCode;

/** A 409 `request_not_pending`: the request's status now (null when the body does not say),
 *  or undefined for any other error. */
export function notPendingStatus(e: unknown): TenantRequestStatus | null | undefined {
  if (!(e instanceof ConsoleApiError) || e.status !== 409 || e.body?.code !== REQUEST_NOT_PENDING)
    return undefined;
  const status = (e.body as Partial<TenantRequestErrorBody>).status;
  return typeof status === 'string' && status in TENANT_REQUEST_STATUS_LABELS ? status : null;
}

/** The first DRF field error of a refused body (`{note: ["..."]}`), if any. */
export function fieldError(e: unknown, field: string): string | null {
  if (!(e instanceof ConsoleApiError) || e.status !== 400) return null;
  const v = (e.body as Record<string, unknown> | undefined)?.[field];
  if (typeof v === 'string') return v;
  return Array.isArray(v) && typeof v[0] === 'string' ? v[0] : null;
}
